import {
	createStandaloneToast as tempoCreateStandaloneToast,
	useBreakpointValue,
	useToast as useTempoToast,
} from '@tempo/core';
import {useMemo} from 'react';
import D4CToolsStorage from '@components/D4CTools/D4CToolsStorage';
import {ConfigKeys} from '@components/D4CTools/types';

type UseToastOptions = Parameters<typeof useTempoToast>[0] & {
	uniqueById?: boolean;
};

type CreateStandAloneToastParam = Parameters<
	typeof tempoCreateStandaloneToast
>[0] & {maxToasts?: number; uniqueById?: boolean};

type ToastId = string | number;

export type TempoCreateToastReturnFn = ReturnType<typeof useTempoToast>;

export type CreateToastReturnFn = ReturnType<typeof useTempoToast> & {
	canSendToast: (toastRegistry: ToastId[]) => boolean;
};

export type CreateStandaloneToastReturnFn = ReturnType<
	typeof tempoCreateStandaloneToast
>;

let toastRegistry: ToastId[] = [];

export const createStandaloneToast = (
	defaultConfig?: CreateStandAloneToastParam,
): CreateStandaloneToastReturnFn => {
	const maxToasts = defaultConfig?.maxToasts || 4;
	const {uniqueById} = defaultConfig || {};

	const {toast: tempoToast, ToastContainer} = tempoCreateStandaloneToast({
		...defaultConfig,
		defaultOptions: {
			duration: 9000,
			isClosable: true,
			...defaultConfig?.defaultOptions,
		},
	});

	const toast = (options?: UseToastOptions) => {
		const id = String(options?.id ?? new Date().getTime());

		if (
			uniqueById &&
			(tempoToast.isActive(id) || toastRegistry.some(tId => tId === id))
		) {
			return id;
		}

		const handleOnCloseComplete = () => {
			options?.onCloseComplete?.();
			toastRegistry = toastRegistry.filter(tId => tId !== id);
		};

		if (toastRegistry.length >= maxToasts) {
			window.setTimeout(() => {
				if (toastRegistry[0] !== undefined) {
					tempoToast.close(toastRegistry[0]);
				}
			}, 500);
		}

		if (D4CToolsStorage.getConfigValue(ConfigKeys.DISABLE_TOAST)) {
			return -1;
		}

		const toastId = tempoToast({
			...options,
			id,
			onCloseComplete: handleOnCloseComplete,
		});

		toastRegistry.push(toastId);
		return toastId;
	};

	toast.update = tempoToast.update;
	toast.promise = tempoToast.promise;
	toast.closeAll = tempoToast.closeAll;
	toast.close = tempoToast.close;
	toast.isActive = tempoToast.isActive;

	return {
		ToastContainer,
		toast,
	};
};

const generateLimitedToast = (
	tempoToast: TempoCreateToastReturnFn,
	maxToasts: number,
	uniqueById?: boolean,
): CreateToastReturnFn => {
	const toast = (options?: UseToastOptions) => {
		const id = String(options?.id ?? new Date().getTime());

		if (
			uniqueById &&
			(tempoToast.isActive(id) || toastRegistry.some(tId => tId === id))
		) {
			return id;
		}

		const handleOnCloseComplete = () => {
			options?.onCloseComplete?.();
			toastRegistry = toastRegistry.filter(tId => tId !== id);
		};

		if (toast.canSendToast(toastRegistry)) {
			window.setTimeout(() => {
				if (toastRegistry[0] !== undefined) {
					tempoToast.close(toastRegistry[0]);
				}
			}, 500);
		}

		if (D4CToolsStorage.getConfigValue(ConfigKeys.DISABLE_TOAST)) {
			return -1;
		}

		const toastId = tempoToast({
			...options,
			id,
			onCloseComplete: handleOnCloseComplete,
		});

		toastRegistry.push(toastId);
		return toastId;
	};

	toast.update = tempoToast.update;
	toast.promise = tempoToast.promise;
	toast.closeAll = tempoToast.closeAll;
	toast.close = tempoToast.close;
	toast.isActive = tempoToast.isActive;
	toast.canSendToast = (registry: ToastId[]) => registry.length >= maxToasts;

	return toast;
};

export const useToast = (
	defaultOptions?: UseToastOptions,
): CreateToastReturnFn => {
	const maxToasts = useBreakpointValue({base: 3, md: 4}, 'md') || 4;
	const {uniqueById} = defaultOptions || {};

	const options = useMemo(
		() => ({
			duration: 9000,
			isClosable: true,
			...defaultOptions,
		}),
		[defaultOptions],
	);

	const tempoToast = useTempoToast(options);

	return useMemo(
		() => generateLimitedToast(tempoToast, maxToasts, uniqueById),
		[maxToasts, tempoToast, uniqueById],
	);
};

export default useToast;
